/**
 * CFO-grade classNames for react-select v5.
 * Uses semantic tokens only: bg-surface, border-border, text-text, ring-ring, etc.
 * No hardcoded hex or Tailwind palette colors.
 *
 * @param {object} opts
 * @param {string} [opts.status] - "error" | "warning" | "success" | undefined
 * @returns {object} classNames map for react-select
 */
export function getCFOSelectClassNames(opts = {}) {
	const { status } = opts;

	const controlBorderBase = "border border-border";
	const controlBorderHover = "hover:border-border-strong";
	const controlFocusRing =
		"ring-2 ring-ring ring-offset-0 ring-offset-bg border-border-strong";
	const controlStatusFocus = {
		error: "ring-2 ring-danger ring-offset-0 ring-offset-bg border-danger",
		warning:
			"ring-2 ring-warning ring-offset-0 ring-offset-bg border-warning",
		success:
			"ring-2 ring-success ring-offset-0 ring-offset-bg border-success",
	};
	const controlStatusClosed = {
		error: "border-danger hover:border-danger",
		warning: "border-warning hover:border-warning",
		success: "border-success hover:border-success",
	};

	const controlBase =
		"inline-flex w-full items-center min-h-9 rounded-md px-3 bg-surface border transition-colors duration-150 text-sm text-text";
	const controlDisabled =
		"bg-surface-2 text-text-3 cursor-not-allowed opacity-100 border-border";

	return {
		control: ({ isDisabled, isFocused, menuIsOpen }) => {
			const open = isFocused || menuIsOpen;
			const parts = [
				controlBase,
				isDisabled && controlDisabled,
			];
			if (!isDisabled) {
				if (open && status && controlStatusFocus[status]) {
					parts.push(controlStatusFocus[status]);
				} else if (open) {
					parts.push(controlFocusRing);
				} else if (status && controlStatusClosed[status]) {
					parts.push(controlStatusClosed[status]);
				} else {
					parts.push(controlBorderBase, controlBorderHover);
				}
			}
			return parts.filter(Boolean).join(" ");
		},
		valueContainer: () =>
			"flex-1 min-w-0 py-1",
		placeholder: () => "text-text-3",
		singleValue: () => "text-text",
		input: () => "text-text",
		indicatorsContainer: () => "flex items-center gap-1",
		dropdownIndicator: ({ isDisabled, menuIsOpen }) => {
			if (isDisabled) return "text-text-3 cursor-not-allowed";
			return [
				"text-text-3 hover:text-text-2",
				menuIsOpen && "text-text-2",
			]
				.filter(Boolean)
				.join(" ");
		},
		clearIndicator: ({ isDisabled }) =>
			isDisabled
				? "text-text-3 cursor-not-allowed"
				: "text-text-3 hover:text-text-2",
		indicatorSeparator: () =>
			"bg-border w-px self-stretch my-2",
		menu: () =>
			"mt-2 bg-surface border border-border rounded-md shadow-2 overflow-hidden z-50",
		menuList: () => "p-1 max-h-72 overflow-auto",
		groupHeading: () =>
			"text-xs font-bold text-text-2 border-b border-border py-2 px-2",
		option: ({ isDisabled, isFocused, isSelected }) => {
			if (isDisabled) {
				return "px-3 py-2 text-sm rounded-sm cursor-not-allowed select-none text-text-3";
			}
			if (isSelected) {
				return "px-3 py-2 text-sm rounded-sm cursor-default select-none text-text-2 bg-primary-soft text-primary";
			}
			if (isFocused) {
				return "px-3 py-2 text-sm rounded-sm cursor-default select-none text-text-2 bg-surface-2";
			}
			return "px-3 py-2 text-sm rounded-sm cursor-default select-none text-text-2";
		},
		noOptionsMessage: () => "px-3 py-2 text-sm text-text-3",
		loadingMessage: () => "px-3 py-2 text-sm text-text-3",
		multiValue: () =>
			"bg-surface-2 border border-border rounded-sm px-2 py-0.5",
		multiValueLabel: () => "text-xs text-text-2",
		multiValueRemove: () => "text-text-3 hover:text-text-2",
		menuPortal: () => "z-[9999]",
	};
}

/**
 * Default props to spread onto react-select for CFO styling.
 * Use with: <Select {...getCFOSelectProps({ status })} ... />
 */
export function getCFOSelectProps(opts = {}) {
	const { status } = opts;
	return {
		unstyled: true,
		classNamePrefix: "cfo-select",
		classNames: getCFOSelectClassNames({ status }),
	};
}
